/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	LatestNewsSectionFragment,
	CosmosEnumLatestNewsVariants,
	CosmosEnumBlogCardsMaxSize,
} from "@cosmos/hygraph-schema";
import { BlogCardProps, BlogCardsSection, FeedItem, NewsCardProps, NewsCardsSection } from "@components/elements";
import { FeaturedBlogCardsSection } from "./FeaturedBlogCards.section";
import { MediumFeaturedSection } from "./MediumFeatured.section";

export type ExternalLatestNewsData =
	| {
			cards: BlogCardProps[];
	  }
	| { cards: NewsCardProps[] }
	| { cards: FeedItem[] }
	/** Optional: `getExternalLatestNewsData` leaves this unset when the feed fetch fails. */
	| { cards?: null };

export type LatestNewsSectionAndExternalProps = LatestNewsSectionFragment &
	ExternalLatestNewsData & {
		feedBrand?: string | null;
		disableCategoryPills?: boolean | null;
		/** True when a page hero renders above this section — forwarded to the featured card so it stays an h2. */
		hasHero?: boolean;
	};

export const LatestNewsSection = ({
	id,
	config,
	cards = [],
	feedBrand,
	featuredLabel,
	latestNewsVariant,
	disableCategoryPills,
	hasHero,
}: LatestNewsSectionAndExternalProps): React.JSX.Element | null => {
	const hideCategoryPills = Boolean(disableCategoryPills);

	if (!cards?.length) return null;
	switch (latestNewsVariant) {
		case CosmosEnumLatestNewsVariants.Large5: {
			return (
				<FeaturedBlogCardsSection
					__typename="CosmosSectionBlogCard"
					id={id}
					config={config}
					cards={cards.slice(0, 5) as BlogCardProps[]}
				/>
			);
		}
		case CosmosEnumLatestNewsVariants.MediumFeatured4:
		case CosmosEnumLatestNewsVariants.MediumFlipFeatured4:
		case CosmosEnumLatestNewsVariants.MediumFeatured10:
		case CosmosEnumLatestNewsVariants.MediumFlipFeatured10: {
			return (
				<MediumFeaturedSection
					id={id}
					config={config}
					latestNewsVariant={latestNewsVariant}
					featuredLabel={featuredLabel}
					// Safe: `getExternalLatestNewsData` returns raw `FeedItem`s for every Featured variant.
					items={cards as FeedItem[]}
					feedBrand={feedBrand}
					hideCategoryPills={hideCategoryPills}
					hasHero={hasHero}
				/>
			);
		}
		case CosmosEnumLatestNewsVariants.Medium3: {
			return (
				<BlogCardsSection
					config={config}
					cards={cards.slice(0, 3) as BlogCardProps[]}
					maxSize={CosmosEnumBlogCardsMaxSize.Three}
					hideCategoryPills={hideCategoryPills}
				/>
			);
		}
		case CosmosEnumLatestNewsVariants.Small4: {
			return (
				<NewsCardsSection
					config={config}
					cards={cards.slice(0, 4) as NewsCardProps[]}
					maxCards={4}
					hideCategoryPills={hideCategoryPills}
				/>
			);
		}
		case CosmosEnumLatestNewsVariants.Medium4Up: {
			return (
				<BlogCardsSection
					config={config}
					cards={cards.slice(0, 4) as BlogCardProps[]}
					maxSize={CosmosEnumBlogCardsMaxSize.Four}
					hideCategoryPills={hideCategoryPills}
				/>
			);
		}
		default: {
			return (
				<NewsCardsSection
					config={config}
					cards={cards.slice(0, 2) as NewsCardProps[]}
					maxCards={2}
					hideCategoryPills={hideCategoryPills}
				/>
			);
		}
	}
};
